import { formatQty } from "../lib/format";
import { SourceBadge } from "./SourceBadge";

export type CoverageSourceTone =
  | "default"
  | "pdf"
  | "ugl"
  | "datanorm"
  | "identity"
  | "review"
  | "manual"
  | "context"
  | "buyer";

export type CoverageSourceChip = {
  key: string;
  label: string;
  active?: boolean | null;
  tone?: CoverageSourceTone;
};

export function SourceCoveragePills({
  sources,
  emptyLabel = "–",
  inactiveMode = "hide",
  ariaLabel,
  className = "",
}: {
  sources: CoverageSourceChip[];
  emptyLabel?: string;
  inactiveMode?: "hide" | "show";
  ariaLabel?: string;
  className?: string;
}) {
  const visibleSources =
    inactiveMode === "show" ? sources : sources.filter((source) => source.active);
  if (visibleSources.length === 0) {
    if (!emptyLabel) return null;
    return <span className="text-slate-400">{emptyLabel}</span>;
  }
  return (
    <div
      className={`flex flex-wrap gap-1 ${className}`}
      aria-label={ariaLabel}
    >
      {visibleSources.map((source) => (
        <SourceBadge
          key={source.key}
          kind={source.tone ?? "default"}
          label={source.label}
          inactive={source.active === false || source.active === null}
          size="xs"
        />
      ))}
    </div>
  );
}

export function CoverageStatusBadge({ status }: { status: string }) {
  const style =
    status === "complete"
      ? "bg-emerald-50 text-emerald-800"
      : status === "review_needed"
        ? "bg-amber-50 text-amber-800"
        : status === "source_gap"
          ? "bg-rose-50 text-rose-700"
          : status === "unsupported"
            ? "bg-slate-100 text-slate-700"
            : "bg-slate-50 text-slate-500";
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${style}`}>
      {coverageStatusLabel(status)}
    </span>
  );
}

export function CoverageReasonPill({ code }: { code: string }) {
  return (
    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
      {coverageReasonLabel(code)}
    </span>
  );
}

export function CoverageReasonPills({
  codes,
  emptyLabel = "–",
  className = "",
}: {
  codes: string[];
  emptyLabel?: string;
  className?: string;
}) {
  if (codes.length === 0) {
    return <span className="text-slate-400">{emptyLabel}</span>;
  }
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {codes.map((code) => (
        <CoverageReasonPill key={code} code={code} />
      ))}
    </div>
  );
}

export function CoverageFact({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-0.5 font-medium text-slate-800">
        {formatCoverageCount(value)}
      </div>
    </div>
  );
}

export function splitCoverageCodes(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function formatCoverageCount(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined) return "–";
  return formatQty(value);
}

export function coverageStatusLabel(status: string): string {
  if (status === "complete") return "Vollständig";
  if (status === "review_needed") return "Prüfung offen";
  if (status === "source_gap") return "Quellenlücke";
  if (status === "unsupported") return "Quellen-Limit";
  return "Unbekannt";
}

export function coverageReasonLabel(code: string): string {
  const labels: Record<string, string> = {
    catalog_without_transactions: "Katalog ohne Transaktionen",
    manual_without_source_evidence: "Manuell ohne Quellenbeleg",
    missing_manufacturer: "Hersteller fehlt",
    no_datanorm_catalog: "Kein DATANORM-Katalog",
    no_pdf_transactions: "Keine PDF-Belege",
    no_ugl_transactions: "Keine UGL-Zeilen",
    pdf_only_context: "Nur PDF-Kontext",
    transactions_without_catalog: "Transaktionen ohne Katalog",
    ugl_cost_center_context: "UGL-Kontext nicht verfügbar",
    unconfirmed_relationship: "Zuordnung offen",
    unmapped_customer_reference: "Kundenreferenz nicht zugeteilt",
    unmapped_delivery_address: "Lieferadresse nicht zugeteilt",
    weak_identity: "Identität prüfen",
  };
  return labels[code] ?? code;
}
